/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node, Waiting, IncrementalQuantity } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    IBuyboxAddToCartViewProps,
    IBuyboxAddToWishlistViewProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps
} from '@msdyn365-commerce-modules/buybox/src/modules/quickview/../../common';
import { IQuickviewViewProps } from '@msdyn365-commerce-modules/buybox/src/modules/quickview/./quickview';
import { IQuickviewProps } from '../definition-extensions/quickview.ext.props.autogenerated';
import { IQuickviewData } from '@msdyn365-commerce-modules/buybox/src/modules/quickview/quickview.data';
import { generateImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import { trafficLightsAtpaction, TrafficLightsAtpInput } from '../../../actions/traffic-lights-atp.action';
import { ATPMessage, TrafficLights } from '../../../utils/atp-utils';
import EstimatedShipDateComponent from '../../../utils/ATP/atpESD';
import { KitProduct } from '../../../utils/Kits/kits';
import { kRV_GetMultipleATPLeadTimeDateAsync } from '../../../actions/customProxy/ATP-update/DataActionExtension.g';
import {
    IKRV_ATPLeadTimePropertiesDate,
    IKRV_GetMultipleATPLeadTimeResponseDate
} from '../../../actions/customProxy/ATP-update/DataServiceEntities.g';
import EstimatedShipDateKitComponent from '../../../utils/ATP/atpKitESD';
import { IBuyboxState, IBuyboxCommonData, IBuyboxExtentedProps } from '@msdyn365-commerce-modules/buybox';
import { PriceComponent } from './components/price.component';
import { filteredProductAttributeList, incExcPriceFormat, isRequestPrice, taxPercentage } from '../../../utils/helper';
import { PromotionBadges } from '../../../utils/badges';

const _renderAddToWishlist = (addToWishlist: IBuyboxAddToWishlistViewProps): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToWishlist;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

const _renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>
            {select}
        </Node>
    );
};

const _renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;
    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

const _renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(_renderConfigureDropdown)}</Node>;
};
interface IQuickviewViewPropsExtended extends IQuickviewViewProps {
    cart?: any;
}
interface CommercePropertyValue {
    BooleanValue?: boolean;
    ByteValue?: number;
    DateTimeOffsetValue?: Date;
    DecimalValue?: number;
    IntegerValue?: number;
    LongValue?: number;
    StringValue?: string;
}
interface CommerceProperty {
    Key?: string;
    Value?: CommercePropertyValue;
}
interface AttributeSwatch {
    SwatchValue?: string;
    SwatchImageUrl?: string;
    SwatchColorHexCode?: string;
    IsDefault?: boolean;
    ProductImageUrls?: string[];
    ExtensionProperties?: CommerceProperty[];
}
interface AttributeValue {
    Name?: string;
    KeyName?: string;
    UnitOfMeasureSymbol?: string;
    DataTypeValue?: number;
    BooleanValue?: boolean;
    DateTimeOffsetValue?: Date;
    FloatValue?: number;
    IntegerValue?: number;
    TextValue?: string;
    CurrencyValue?: number;
    CurrencyCode?: string;
    AttributeValueId?: number;
    RecordId?: number;
    Swatches?: AttributeSwatch[];
    ExtensionProperties?: CommerceProperty[];
}
interface customAttribute {
    attributevalues?: AttributeValue[];
}

/**
 * Functional component which renders quick view button and popup based by the given props file.
 * @param {IQuickviewViewProps} props - Configuration for the quick view component.
 * @returns {React.FC<IQuickviewViewProps>} - Functional component of the quick view.
 */
export const QuickViewFunctionalComponent: React.FC<IQuickviewViewPropsExtended & IQuickviewProps<IQuickviewData>> = (
    props: IQuickviewViewPropsExtended & IQuickviewProps<IQuickviewData> & customAttribute
) => {
    const { quickView, quickViewButton, isModalOpen, isMobileDevice, resources } = props;
    const isUserAuth = props.context.actionContext.requestContext.user.isAuthenticated;
    const [isMax, setIsMax] = React.useState<boolean>(false);
    const [isMin, setIsMin] = React.useState<boolean>(true);
    const [isClassName, setIsClassName] = React.useState<boolean>(true);
    const [producerName, setProducerName] = React.useState<string | undefined>('');
    const [isProductDiscontinued, setIsProductDiscontinued] = React.useState<boolean>(false);
    const [isAddToCartDisabled, setIsAddToCartDisabled] = React.useState<boolean>(false);
    const [disableAddToCartATP, setDisableAddToCartATP] = React.useState<boolean>(false);
    const [twoZeroFourProdMax, setTwoZeroFourProdMax] = React.useState<number>(0);
    //To set Image Url
    const [imgURI, setImgURL] = React.useState<string | undefined>();
    const productAttributes = filteredProductAttributeList(props.attributevalues, props.context.app.config.kcProductAttribute);
    const findProducerName = () => {
        productAttributes?.map(e => {
            if (e.identifier === 'Manufacturer') {
                setProducerName(e?.TextValue);
            }
        });
    };

    //use this function to render or display category image
    const _renderCategoryImg = (imgURI: string) => {
        return (
            <div className='_logo-container'>
                <img src={`${imgURI}.png`} alt='producer-images' className='_logo' />
            </div>
        );
    };
    //To set the producer name
    React.useEffect(() => {
        findProducerName();
        if (producerName) {
            setImgURL(producerName);
        }
    }, [props.attributevalues]);

    //to generate Image url after getting img url from api call
    React.useEffect(() => {
        if (imgURI) {
            let imageUrl = generateImageUrl(imgURI, props.context.actionContext.requestContext.apiSettings);
            imageUrl = imageUrl?.replace('=/', '=') || '';
            setImgURL(imageUrl);
        }
    }, [imgURI]);

    React.useEffect(() => {
        props.state.quantity <= props.state.min! && props.state.quantity !== 1 ? setIsMin(true) : setIsMin(false);
        props.state.quantity === props.state.max! ? setIsMax(true) : setIsMax(false);
        props.state.quantity < props.state.min! ? setIsClassName(true) : setIsClassName(false);
    }, [props.state.quantity, props.state.min, props.state.max]);

    const _renderAddToCart = (addToCart: IBuyboxAddToCartViewProps): JSX.Element => {
        const { ContainerProps, errorBlock, button } = addToCart;
        let customAddBundleToCartButton;
        if (props?.productDetails?.product?.Components?.length !== 0) {
            customAddBundleToCartButton =
                button &&
                React.cloneElement(button as JSX.Element, {
                    title: props.resources.addBundleToCartButtonLabel,
                    children: props.resources.addBundleToCartButtonLabel,
                    addToCartText: props.resources.addBundleToCartButtonLabel
                });
        }
        return (
            <Node {...ContainerProps}>
                {errorBlock}
                <div className='buttons'>
                    {props.productDetails?.product?.Components?.length !== 0 ? (
                        <button className='msc-add-to-cart' disabled={true}>
                            {props.resources.addBundleToCartButtonLabel}
                        </button>
                    ) : isRequestPrice(
                          props.productDetails?.productPrice!,
                          props.data.customerInformation.result?.LineDiscountGroup!,
                          props.productDetails?.product?.Components?.length !== 0
                      ) ||
                      isClassName ||
                      isProductDiscontinued ||
                      isAddToCartDisabled ||
                      disableAddToCartATP ? (
                        <button className='msc-add-to-cart ' disabled={true}>
                            {props.resources.addToCartbuttonLabel}
                        </button>
                    ) : props?.productDetails?.product?.Components?.length !== 0 ? (
                        <div>{customAddBundleToCartButton}</div>
                    ) : (
                        <div>{button}</div>
                    )}
                </div>
            </Node>
        );
    };

    const _renderQuantity = (
        quantity: IBuyboxProductQuantityViewProps,
        quantityLimitsMessages: React.ReactNode,
        propMax: number
    ): JSX.Element => {
        const { ContainerProps, LabelContainerProps, heading, input, errors } = quantity;
        const customInput =
            input &&
            React.cloneElement(input as JSX.Element, {
                max: propMax,
                disable: disableAddToCartATP ? true : false,
                min: 1
            });

        return (
            <Node {...ContainerProps}>
                <Node {...LabelContainerProps}>
                    {heading}
                    {errors}
                </Node>
                {isRequestPrice(
                    props.productDetails?.productPrice!,
                    props.data.customerInformation.result?.LineDiscountGroup!,
                    props.productDetails?.product?.Components?.length !== 0
                ) ||
                isProductDiscontinued ||
                isAddToCartDisabled ||
                disableAddToCartATP ? (
                    <IncrementalQuantity disabled={true} />
                ) : (
                    customInput
                )}
                {/* {quantityLimitsMessages} */}
                <div className='quantity_text_message'>
                    {props.state.min && isMin && (
                        <p className='ms-buybox__minmax_label_text'>
                            <span className='sr-only'>{props.resources.informationHeading}</span>
                            <i className='msi-InfoCircle' aria-hidden='true'></i>
                            {`${props.resources.minErrorMessage} ${props.state.min}`}
                        </p>
                    )}

                    {(isMax || disableAddToCartATP) && !isMin && (
                        <p className='ms-buybox__minmax_label_text'>
                            <span className='sr-only'>{props.resources.informationHeading}</span>
                            <i className='msi-InfoCircle' aria-hidden='true'></i>
                            {disableAddToCartATP
                                ? props.resources.maxQuantityMessage.replace('{propMax}', String(propMax))
                                : `${props.resources.maxErrorMessage} ${twoZeroFourProdMax !== 0 ? twoZeroFourProdMax : props.state.max}`}
                        </p>
                    )}
                </div>
            </Node>
        );
    };

    const RenderBodyContent: React.FC<IQuickviewViewPropsExtended & customAttribute> = (
        props: IQuickviewViewPropsExtended & customAttribute
    ) => {
        const {
            title,
            price,
            rating,
            keyInPrice,
            quantity,
            configure,
            inventoryLabel,
            quantityLimitsMessages,
            // seeDetailsbutton,
            loading,
            ProductInfoContainerProps,
            MediaGalleryContainerProps,
            CarouselProps,
            unitOfMeasure,
            productDetails,
            cart
        } = props;
        const buyBoxProps = props as IBuyboxExtentedProps<IBuyboxCommonData>;
        const { enabledIncExPrices } = props.context.app.config;
        const renderItemNumber = () => {
            let itemNumber;
            itemNumber = props.productDetails?.product?.ItemId;
            return `${itemNumber}`;
        };
        const customerTaxPercentage = taxPercentage(props.data.customerInformation?.result!);
        const calculateWpPrice = () => {
            let price;
            let discountedWpPrice: number;
            let displayValue;

            props.attributevalues?.map(e => {
                if (e.KeyName === 'PowerratingPmax(Wp)') {
                    price = props.productDetails?.productPrice?.CustomerContextualPrice! / e.IntegerValue!;
                    discountedWpPrice = props.productDetails?.productPrice?.CustomerContextualPrice! / e.IntegerValue!;
                }
                if (e.KeyName === 'DisplayWPPrice') {
                    displayValue = e.BooleanValue;
                }
            });
            if (displayValue && price !== undefined) {
                const ppwPrice =
                    props?.context?.actionContext?.requestContext?.apiSettings?.oun !== props.context.app.config.ounNumberForUsSite
                        ? resources.wpPriceLabel
                        : resources.ppwPriceLabel;
                return (
                    Number.isFinite(discountedWpPrice!) && (
                        <div>
                            <span className='manufacturer-title'>{ppwPrice}:</span>
                            <span className='manufacturer-name'>
                                {incExcPriceFormat(Number(discountedWpPrice!), props.context, enabledIncExPrices, customerTaxPercentage, 3)}
                            </span>
                        </div>
                    )
                );
            }
            return <></>;
        };

        if (loading) {
            return <>{loading}</>;
        }
        let linkedProducts: any[] | undefined;
        if (productDetails) {
            linkedProducts = productDetails.product?.Components;
        }
        const extensionsProps = props?.productDetails?.product?.ExtensionProperties;
        let promotionBadge = '';
        if (extensionsProps?.find(e => e?.Key === 'PromotionBadge')) {
            promotionBadge = extensionsProps?.find(e => e?.Key === 'PromotionBadge')?.Value?.StringValue || '';
        }
        // ************ VSI Customization Starts for Bundle kit ATP ****************//
        const [atpData, setAtpData] = React.useState<Array<IKRV_GetMultipleATPLeadTimeResponseDate | undefined>>([]);
        const [kitProductATP, setKitProductATP] = React.useState<boolean>(false);
        const [isKitProductATPDiscontinued, setIsKitProductATPDiscontinued] = React.useState<boolean>(false);
        const _atpData = async (products: any[] | undefined) => {
            const KRVATPLeadTimeDate: any[] = [];
            //Create date according to API
            const objectDate = new Date();
            const day = objectDate.getDate();
            const month = objectDate.getMonth();
            const year = objectDate.getFullYear();
            const requestedDate = `${day}/${month + 1}/${year}`;

            if (products && products.length > 0) {
                products?.map((e: any) => {
                    KRVATPLeadTimeDate.push({
                        itemID: e.ItemId,
                        requestedDate: requestedDate
                    });
                });
            }
            const ctx = { callerContext: props.context.actionContext };
            try {
                const response = await kRV_GetMultipleATPLeadTimeDateAsync(ctx, KRVATPLeadTimeDate);
                setAtpData([response] || []);
                setKitProductATP(true);
            } catch (err) {
                console.error(err, 'ERR');
            }
        };
        function kitProductATPCall() {
            if (linkedProducts?.length !== 0) {
                _atpData(linkedProducts);
            }
        }

        React.useEffect(() => {
            if (linkedProducts?.length !== 0 && props.context.request.user.isAuthenticated) {
                if (!kitProductATP) {
                    kitProductATPCall();
                }
                const anyRed = atpData[0]?.ATPTrafficLights?.filter(item => item.TrafficLight === 'Red');
                if (anyRed?.length || trafficLightsResponse?.IsDiscontinuedProduct === 'Yes') {
                    setIsKitProductATPDiscontinued(true);
                    setIsProductDiscontinued(true);
                }
                if (trafficLightsResponse?.IsAddToCart === 'No') {
                    setIsAddToCartDisabled(true);
                }
            }
        }, [linkedProducts, atpData]);
        // ************ VSI Customization Ends for Bundle kit ATP ****************//
        // ********** VSI Customization Start ****************//
        const itemId = productDetails?.product?.ItemId;
        const [isLoading, setIsLoading] = React.useState<boolean>(true);
        const [propMax, setPropMax] = React.useState<number>(props.state.max!);
        const [maxESD, setMaxESD] = React.useState<number>(props.state.max!);
        const [trafficLightsResponse, settrafficLightsResponse] = React.useState<IKRV_ATPLeadTimePropertiesDate>();

        React.useEffect(() => {
            if (props.state.max) {
                setPropMax(props.state.max);
            }
        }, [props.state.max]);

        //Use this Effect for set max values of product in state according to status
        React.useEffect(() => {
            if (trafficLightsResponse) {
                if (trafficLightsResponse.StatusCode === '500') {
                    setIsClassName(true);
                    setPropMax(trafficLightsResponse?.Quantity);
                    setMaxESD(trafficLightsResponse?.Quantity);
                } else if (trafficLightsResponse.StatusCode === '501') {
                    if (trafficLightsResponse.POQuantity > props.state.max!) {
                        setPropMax(props.state.max!);
                        setMaxESD(props.state.max!);
                    } else {
                        setPropMax(trafficLightsResponse.POQuantity!);
                        setMaxESD(trafficLightsResponse.POQuantity!);
                    }
                } else if (trafficLightsResponse.StatusCode === '502') {
                    setPropMax(trafficLightsResponse?.Quantity);
                    setMaxESD(trafficLightsResponse?.Quantity);
                } else if (trafficLightsResponse.StatusCode === '503') {
                    if (trafficLightsResponse?.Quantity + trafficLightsResponse.POQuantity > props.state.max!) {
                        setPropMax(props.state.max!);
                        setMaxESD(trafficLightsResponse?.Quantity + trafficLightsResponse.POQuantity);
                    } else {
                        setPropMax(trafficLightsResponse?.Quantity + trafficLightsResponse.POQuantity);
                        setMaxESD(trafficLightsResponse?.Quantity + trafficLightsResponse.POQuantity);
                    }
                } else if (
                    trafficLightsResponse.StatusCode === '204' &&
                    trafficLightsResponse?.Message?.toLocaleLowerCase() === resources.noFurtherSuppliesText
                ) {
                    //checking if already same product exist in cart
                    let productOfInterestQuantity: number | undefined;

                    cart?.result?.cart?.CartLines?.map((item: { ItemId: string | undefined; Quantity: number | undefined }) => {
                        if (item.ItemId === itemId) {
                            productOfInterestQuantity = item.Quantity;
                        }
                        return undefined;
                    });
                    setPropMax(
                        productOfInterestQuantity !== undefined
                            ? trafficLightsResponse?.Quantity - productOfInterestQuantity!
                            : trafficLightsResponse?.Quantity
                    );
                    setMaxESD(trafficLightsResponse?.Quantity);
                } else {
                    setPropMax(props.state.max!);
                }
            }
        }, [trafficLightsResponse]);
        React.useEffect(() => {
            if (cart?.result?.cart?.CartLines && trafficLightsResponse?.StatusCode === '204') {
                //checking if already same product exist in cart
                let productOfInterestQuantity: number | undefined;
                cart?.result?.cart?.CartLines?.map((item: { ItemId: string | undefined; Quantity: number | undefined }) => {
                    if (item.ItemId === itemId) {
                        productOfInterestQuantity = item.Quantity;
                    }
                    return undefined;
                });
                //condition to disable add to cart
                if (productOfInterestQuantity && productOfInterestQuantity >= trafficLightsResponse?.Quantity!) {
                    setDisableAddToCartATP(true);
                    setPropMax(trafficLightsResponse?.Quantity!);
                }
            }
        }, [cart?.result?.cart, trafficLightsResponse]);
        React.useEffect(() => {
            if (trafficLightsResponse?.StatusCode === '204') {
                //condition to set new max
                if (
                    (props.state.max && props.state.max > trafficLightsResponse?.Quantity!) ||
                    props.state.quantity === trafficLightsResponse?.Quantity!
                ) {
                    setTwoZeroFourProdMax(trafficLightsResponse?.Quantity!);
                    setIsMax(true);
                } else if (props.state.quantity < trafficLightsResponse?.Quantity!) {
                    setIsMax(false);
                }
            }
        }, [props.state.quantity, trafficLightsResponse]);
        //VSI Customization Start States of traffic lights
        let objectDate = new Date();
        let day = objectDate.getDate();
        let month = objectDate.getMonth();
        let year = objectDate.getFullYear();
        let requestedDate = `${month + 1}/${day}/${year}`;

        //VSI Customization END States of traffic lights
        async function trafficLightsDataActionCall() {
            setIsLoading(true);
            const requestBody = {
                itemId: itemId!,
                requestedDate: requestedDate
            };
            try {
                const trafficLightsParams = new TrafficLightsAtpInput(requestBody.itemId, requestBody.requestedDate);
                const trafficLightsSlots = await trafficLightsAtpaction(trafficLightsParams, props.context.actionContext);
                settrafficLightsResponse(trafficLightsSlots);
                if (trafficLightsSlots.TrafficLight === 'Red' || trafficLightsResponse?.IsDiscontinuedProduct === 'Yes') {
                    setIsProductDiscontinued(true);
                }
                if (trafficLightsResponse?.IsAddToCart === 'No') {
                    setIsAddToCartDisabled(true);
                }
            } catch (error) {
                props.telemetry.warning('error');
                props.telemetry.debug('Unable to get response for traffic lights slots');
            }
            setIsLoading(false);
        }

        React.useEffect(() => {
            if (requestedDate && itemId) trafficLightsDataActionCall();
        }, [requestedDate, itemId]);

        // ********** VSI Customization END ****************//

        if (CarouselProps.items) {
            if (CarouselProps.items[0]?.props.children) {
                CarouselProps.items[0].props.children.props.role = '';
            }
        }

        return (
            <>
                <PromotionBadges context={props.context} promotionalBadge={promotionBadge} />
                <Node {...MediaGalleryContainerProps}>
                    <Node {...CarouselProps} />
                </Node>
                <Node {...ProductInfoContainerProps}>
                    <div className='producer__ImageURI'>{imgURI && _renderCategoryImg(imgURI)}</div>
                    {title}
                    <div>
                        <span className='manufacturer-title'>{resources.itemNnLabel}: </span>
                        <span className='item-number'>{renderItemNumber()}</span>
                    </div>
                    {linkedProducts?.length !== 0 ? (
                        <KitProduct linkedProducts={linkedProducts} props={props} />
                    ) : (
                        <>
                            {unitOfMeasure}
                            {rating}
                            {configure && _renderConfigure(configure)}
                            {keyInPrice && _renderKeyInPrice(keyInPrice)}
                        </>
                    )}
                    {/* *********** VSI CUSTOMIZATION START FOR ATP ************ */}
                    <div className='quickview-atp-wrapper'>
                        {isLoading ? (
                            <Waiting />
                        ) : (
                            <div className='atp-pdp-container'>
                                {trafficLightsResponse && (
                                    <div className='lights-section'>
                                        <TrafficLights
                                            lightColor={
                                                linkedProducts?.length !== 0
                                                    ? 'Yellow'
                                                    : isKitProductATPDiscontinued
                                                    ? 'Red'
                                                    : trafficLightsResponse?.TrafficLight
                                            }
                                            isVisible={trafficLightsResponse?.IsVisible}
                                        />
                                        {linkedProducts?.length !== 0 ? (
                                            <div className='_message-container'>
                                                <h4 className='_traffic-light-quantity'>{resources.contactSalesOfficeMessage} </h4>
                                            </div>
                                        ) : isKitProductATPDiscontinued ? (
                                            <div className='_message-container'>
                                                <h4 className='_traffic-light-quantity'>{resources.noFurtherSuppliesText} </h4>
                                            </div>
                                        ) : (
                                            <ATPMessage
                                                {...trafficLightsResponse}
                                                locale={props.context.app.config.dateFormat}
                                                currency={props.context.request.channel?.Currency}
                                            />
                                        )}
                                    </div>
                                )}

                                {linkedProducts?.length !== 0 ? (
                                    <EstimatedShipDateKitComponent
                                        linkedProducts={linkedProducts}
                                        itemNumber={itemId}
                                        propMax={maxESD}
                                        context={props.context}
                                        trafficLightsResponse={trafficLightsResponse}
                                        anyChildWithRedLight={isKitProductATPDiscontinued}
                                    />
                                ) : isKitProductATPDiscontinued ? null : (
                                    <EstimatedShipDateComponent
                                        itemNumber={itemId}
                                        propMax={maxESD}
                                        context={props.context}
                                        trafficLightsResponse={trafficLightsResponse}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                    {/* *********** VSI CUSTOMIZATION End FOR ATP ************ */}

                    <div className='quantity-price'>
                        {linkedProducts?.length !== 0 ? (
                            <div className='quantity'>
                                <div className='ms-quickView__product-quantity-label'>{resources.quantityLabel}</div>
                                <button className={`decrement quantity__controls disabled`} />
                                <input type='number' className='quantity-input' value={1} disabled={true} />
                                <button className={`increment quantity__controls disabled`} />
                            </div>
                        ) : (
                            quantity && _renderQuantity(quantity, quantityLimitsMessages, propMax)
                        )}
                        <div className='price-wrapper'>
                            {isRequestPrice(
                                props.productDetails?.productPrice!,
                                props.data.customerInformation?.result?.LineDiscountGroup!,
                                linkedProducts?.length !== 0
                            ) ? (
                                <div className='no-price-wrapper'>
                                    <span className='label'>{resources.requestPriceLabel}</span>
                                    <div className='tooltip-wrapper' tabIndex={0}>
                                        <span className='tooltip-icon' />
                                        <span className='tooltip-text'>{resources.cantAddContactSalesOfficeMessage}</span>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {price && getBuyboxProductPrice(buyBoxProps, props.state)}
                                    {linkedProducts?.length !== 0 ? <p className='bundle-price-label'>{resources.forBundleLabel}</p> : null}
                                </>
                            )}
                            {props.context.actionContext.requestContext.user.isAuthenticated &&
                                !isRequestPrice(
                                    props.productDetails?.productPrice!,
                                    props.data.customerInformation?.result?.LineDiscountGroup!,
                                    linkedProducts?.length !== 0
                                ) &&
                                calculateWpPrice()}
                        </div>
                    </div>
                    {/* {seeDetailsbutton} */}
                    {inventoryLabel}
                </Node>
            </>
        );
    };

    const _renderQuickViewPopup = (props: IQuickviewViewPropsExtended): JSX.Element => {
        const {
            ModalContainer,
            ModalHeaderContainer,
            ModalFooterContainer,
            ModalHeaderContent,
            ModalBodyContainer,
            addToWishlist,
            addToCart,
            cartContainerProps
        } = props;
        return (
            <Module {...ModalContainer}>
                <Node {...ModalHeaderContainer}>{ModalHeaderContent}</Node>
                <Node {...ModalBodyContainer}> {<RenderBodyContent {...props} />}</Node>
                <Node {...ModalFooterContainer}>
                    <Node {...cartContainerProps}>
                        {addToCart && _renderAddToCart(addToCart)}
                        {addToWishlist && _renderAddToWishlist(addToWishlist)}
                    </Node>
                </Node>
            </Module>
        );
    };

    return (
        <Module {...quickView}>
            {!isMobileDevice && isUserAuth && quickViewButton}
            {isModalOpen && _renderQuickViewPopup(props)}
        </Module>
    );
};

const getBuyboxProductPrice = (props: IBuyboxExtentedProps<IBuyboxCommonData>, state: IBuyboxState): React.ReactElement | undefined => {
    const { id, typeName, context, resources } = props;
    const customerTaxPercentage = taxPercentage(props.data.customerInformation?.result!);
    return (
        state.productPrice && (
            <PriceComponent
                id={id}
                typeName={typeName}
                context={context}
                data={{ price: state.productPrice }}
                isPriceMinMaxEnabled={
                    props.data.featureState.result?.find(
                        featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
                    )?.IsEnabled
                }
                isSalesAgreementPriceFeatureEnabled={
                    props.data.featureState.result?.find(
                        featureState => featureState.Name === 'Dynamics.AX.Application.RetailSalesAgreementPriceSupportFeature'
                    )?.IsEnabled
                }
                useLegacySalesAgreementHTMLFeature={
                    props.data.featureState.result?.find(featureState => featureState.Name === 'UseLegacySalesAgreementHTMLFeature')
                        ?.IsEnabled
                }
                priceResources={{
                    priceRangeSeparator: resources.priceRangeSeparator,
                    salesAgreementPricePrompt: resources.salesAgreementPricePrompt,
                    salesAgreementExpirationDatePrompt: resources.salesAgreementExpirationDatePrompt,
                    salesAgreementCommittedQuantityPrompt: resources.salesAgreementCommittedQuantityPrompt,
                    salesAgreementRemainingQuantityPrompt: resources.salesAgreementRemainingQuantityPrompt
                }}
                freePriceText={resources.priceFree}
                originalPriceText={resources.originalPriceText}
                currentPriceText={resources.currentPriceText}
                isIncExPriceFormatEnabled={true}
                customerTaxPercentage={customerTaxPercentage}
            />
        )
    );
};

export default QuickViewFunctionalComponent;
